import { Button, Modal } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import {
  confirmDiscardDraft,
  useUnsavedChangesGuard,
} from "@/lib/utils/unsaved-changes";
import {
  type BuildingDraft,
  type GeoBuilding,
  type GeoZone,
  type GeoZoneType,
  type NewBuildingRow,
  type NewZoneRow,
  YardGeographyTable,
  ZONE_TYPE_LABELS,
  type ZoneDraft,
} from "./yard-geography-table";

const ZONE_TYPE_RANK: Record<GeoZoneType, number> = {
  horizontale: 0,
  verticale: 1,
};

function sortZones(zones: GeoZone[]): GeoZone[] {
  return [...zones].sort(
    (a, b) =>
      ZONE_TYPE_RANK[a.type] - ZONE_TYPE_RANK[b.type] || a.order - b.order,
  );
}

function generateTempId(): string {
  return `tmp-${Math.random().toString(36).slice(2)}`;
}

export type YardGeographyProps = {
  yardId: number;
  /** Whether the yard is still in its preparation period. */
  inPreparation: boolean;
};

export function YardGeography({ yardId, inPreparation }: YardGeographyProps) {
  const [buildings, setBuildings] = useState<GeoBuilding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Draft edits to existing buildings/zones: staged until "Enregistrer".
  const [draftBuildings, setDraftBuildings] = useState<
    Map<number, BuildingDraft>
  >(new Map());
  const [draftZones, setDraftZones] = useState<Map<number, ZoneDraft>>(
    new Map(),
  );

  // Rows added via "+" but not yet created on the server.
  const [newBuildings, setNewBuildings] = useState<NewBuildingRow[]>([]);
  const [newZonesByBuilding, setNewZonesByBuilding] = useState<
    Map<number, NewZoneRow[]>
  >(new Map());

  const [isSaving, setIsSaving] = useState(false);

  const [buildingToDelete, setBuildingToDelete] = useState<GeoBuilding | null>(
    null,
  );
  const [zoneToDelete, setZoneToDelete] = useState<GeoZone | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Confirmation requested when saving edits to existing rows on a yard that
  // is no longer in preparation. Also guards reordering.
  const [pendingModification, setPendingModification] = useState<{
    description: string;
    run: () => void | Promise<void>;
  } | null>(null);

  // `loading` is only true for the initial fetch (its useState default).
  // Refetches after a save/delete/reorder update the table in place: flipping
  // `loading` again would unmount the table for a "Chargement..." placeholder
  // and lose the user's scroll position.
  const fetchData = useCallback(async () => {
    const [buildingsRes, zonesRes] = await Promise.all([
      apiClient.GET("/api/admin/yards/buildings", {
        params: { query: { yardId } },
      }),
      apiClient.GET("/api/admin/yards/zones", {
        params: { query: { yardId } },
      }),
    ]);

    if (buildingsRes.error || zonesRes.error) {
      setError("Erreur lors du chargement du découpage géographique");
      setLoading(false);
      return;
    }

    const zonesByBuilding = new Map<number, GeoZone[]>();
    for (const zone of zonesRes.data.data.zones) {
      const list = zonesByBuilding.get(zone.buildingId) ?? [];
      list.push({
        id: zone.id,
        buildingId: zone.buildingId,
        name: zone.name,
        shortName: zone.shortName,
        type: zone.type as GeoZoneType,
        order: zone.order,
      });
      zonesByBuilding.set(zone.buildingId, list);
    }

    const merged: GeoBuilding[] = buildingsRes.data.data.buildings
      .map((building) => ({
        id: building.id,
        name: building.name,
        shortName: building.shortName,
        order: building.order,
        zones: sortZones(zonesByBuilding.get(building.id) ?? []),
      }))
      .sort((a, b) => a.order - b.order);

    setBuildings(merged);
    setError(null);
    setLoading(false);
    // Refreshed data is the new baseline: any pending draft is now stale.
    setDraftBuildings(new Map());
    setDraftZones(new Map());
    setNewBuildings([]);
    setNewZonesByBuilding(new Map());
  }, [yardId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Runs a modification immediately while in preparation, otherwise asks for
  // confirmation first.
  const guardModification = useCallback(
    (description: string, run: () => void | Promise<void>) => {
      if (inPreparation) {
        void run();
        return;
      }
      setPendingModification({ description, run });
    },
    [inPreparation],
  );

  // --- Draft edits to existing buildings ---

  const getBuildingDraft = (building: GeoBuilding): BuildingDraft =>
    draftBuildings.get(building.id) ?? {
      name: building.name,
      shortName: building.shortName ?? "",
    };

  const isBuildingDirty = (building: GeoBuilding): boolean =>
    draftBuildings.has(building.id);

  const onBuildingFieldChange = (
    building: GeoBuilding,
    patch: Partial<BuildingDraft>,
  ) => {
    setDraftBuildings((prev) => {
      const current = prev.get(building.id) ?? {
        name: building.name,
        shortName: building.shortName ?? "",
      };
      const next = { ...current, ...patch };
      const updated = new Map(prev);
      if (
        next.name === building.name &&
        next.shortName === (building.shortName ?? "")
      ) {
        updated.delete(building.id);
      } else {
        updated.set(building.id, next);
      }
      return updated;
    });
  };

  // --- Draft edits to existing zones ---

  const getZoneDraft = (zone: GeoZone): ZoneDraft =>
    draftZones.get(zone.id) ?? {
      name: zone.name,
      shortName: zone.shortName ?? "",
      type: zone.type,
    };

  const isZoneDirty = (zone: GeoZone): boolean => draftZones.has(zone.id);

  const onZoneFieldChange = (zone: GeoZone, patch: Partial<ZoneDraft>) => {
    setDraftZones((prev) => {
      const current = prev.get(zone.id) ?? {
        name: zone.name,
        shortName: zone.shortName ?? "",
        type: zone.type,
      };
      const next = { ...current, ...patch };
      const updated = new Map(prev);
      if (
        next.name === zone.name &&
        next.shortName === (zone.shortName ?? "") &&
        next.type === zone.type
      ) {
        updated.delete(zone.id);
      } else {
        updated.set(zone.id, next);
      }
      return updated;
    });
  };

  // --- New (not yet created) rows ---

  const onAddBuildingRow = () => {
    setNewBuildings((prev) => [
      ...prev,
      { tempId: generateTempId(), name: "", shortName: "" },
    ]);
  };

  const onNewBuildingFieldChange = (
    tempId: string,
    patch: Partial<BuildingDraft>,
  ) => {
    setNewBuildings((prev) =>
      prev.map((row) => (row.tempId === tempId ? { ...row, ...patch } : row)),
    );
  };

  const onRemoveNewBuilding = (tempId: string) => {
    setNewBuildings((prev) => prev.filter((row) => row.tempId !== tempId));
  };

  const onAddZoneRow = (buildingId: number) => {
    setNewZonesByBuilding((prev) => {
      const updated = new Map(prev);
      const list = updated.get(buildingId) ?? [];
      updated.set(buildingId, [
        ...list,
        {
          tempId: generateTempId(),
          name: "",
          shortName: "",
          type: "horizontale",
        },
      ]);
      return updated;
    });
  };

  const onNewZoneFieldChange = (
    buildingId: number,
    tempId: string,
    patch: Partial<ZoneDraft>,
  ) => {
    setNewZonesByBuilding((prev) => {
      const updated = new Map(prev);
      const list = updated.get(buildingId) ?? [];
      updated.set(
        buildingId,
        list.map((row) => (row.tempId === tempId ? { ...row, ...patch } : row)),
      );
      return updated;
    });
  };

  const onRemoveNewZone = (buildingId: number, tempId: string) => {
    setNewZonesByBuilding((prev) => {
      const updated = new Map(prev);
      const list = updated.get(buildingId) ?? [];
      updated.set(
        buildingId,
        list.filter((row) => row.tempId !== tempId),
      );
      return updated;
    });
  };

  const hasNewRows =
    newBuildings.length > 0 ||
    [...newZonesByBuilding.values()].some((rows) => rows.length > 0);
  const hasExistingEdits = draftBuildings.size > 0 || draftZones.size > 0;
  const isDirty = hasNewRows || hasExistingEdits;

  useUnsavedChangesGuard(isDirty);

  const cancelDraft = () => {
    setDraftBuildings(new Map());
    setDraftZones(new Map());
    setNewBuildings([]);
    setNewZonesByBuilding(new Map());
  };

  const saveAll = async () => {
    setIsSaving(true);
    let failed = false;

    for (const [id, d] of draftBuildings.entries()) {
      const { error: apiError } = await apiClient.PATCH(
        "/api/admin/yards/buildings/{id}",
        { params: { path: { id } }, body: d },
      );
      if (apiError) failed = true;
    }

    for (const [id, d] of draftZones.entries()) {
      const { error: apiError } = await apiClient.PATCH(
        "/api/admin/yards/zones/{id}",
        { params: { path: { id } }, body: d },
      );
      if (apiError) failed = true;
    }

    for (const row of newBuildings) {
      const { error: apiError } = await apiClient.POST(
        "/api/admin/yards/buildings",
        { body: { yardId, name: row.name, shortName: row.shortName } },
      );
      if (apiError) failed = true;
    }

    for (const [buildingId, rows] of newZonesByBuilding.entries()) {
      for (const row of rows) {
        const { error: apiError } = await apiClient.POST(
          "/api/admin/yards/zones",
          {
            body: {
              yardId,
              buildingId,
              name: row.name,
              shortName: row.shortName || undefined,
              type: row.type,
            },
          },
        );
        if (apiError) failed = true;
      }
    }

    setIsSaving(false);
    if (failed) {
      toaster.error({
        title: "Erreur",
        description: "Échec de l'enregistrement de certaines modifications",
      });
    } else {
      toaster.success({
        title: "Succès",
        description: "Modifications enregistrées",
      });
    }
    fetchData();
  };

  const handleSaveClick = () => {
    const incompleteBuilding = [
      ...newBuildings,
      ...draftBuildings.values(),
    ].some((row) => !row.name.trim() || !row.shortName.trim());
    const incompleteZone = [
      ...[...newZonesByBuilding.values()].flat(),
      ...draftZones.values(),
    ].some((row) => !row.name.trim());
    if (incompleteBuilding || incompleteZone) {
      toaster.error({
        title: "Erreur",
        description:
          "Merci de renseigner les cases marquées « Requis » avant d'enregistrer.",
      });
      return;
    }

    if (hasExistingEdits) {
      guardModification("l'enregistrement de ces modifications", saveAll);
    } else {
      void saveAll();
    }
  };

  const reorderBuildings = (orderedIds: number[]) => {
    guardModification("le réordonnancement des bâtiments", async () => {
      const byId = new Map(buildings.map((b) => [b.id, b]));
      setBuildings(
        orderedIds
          .map((id) => byId.get(id))
          .filter((b): b is GeoBuilding => isNotNil(b)),
      );
      const { error: apiError } = await apiClient.PATCH(
        "/api/admin/yards/buildings/reorder",
        { body: { yardId, orderedIds } },
      );
      if (apiError) {
        toaster.error({
          title: "Erreur",
          description: "Échec du réordonnancement des bâtiments",
        });
        fetchData();
      }
    });
  };

  const reorderZones = (
    buildingId: number,
    type: GeoZoneType,
    orderedIds: number[],
  ) => {
    guardModification("le réordonnancement des zones", async () => {
      setBuildings((prev) =>
        prev.map((b) => {
          if (b.id !== buildingId) return b;
          const others = b.zones.filter((z) => z.type !== type);
          const byId = new Map(
            b.zones.filter((z) => z.type === type).map((z) => [z.id, z]),
          );
          // Renumber `order` to match the drop result: sortZones sorts by
          // this field, so keeping the old values would immediately undo
          // the optimistic reorder.
          const reordered = orderedIds
            .map((id) => byId.get(id))
            .filter((z): z is GeoZone => isNotNil(z))
            .map((zone, index) => ({ ...zone, order: index + 1 }));
          return { ...b, zones: sortZones([...others, ...reordered]) };
        }),
      );
      const { error: apiError } = await apiClient.PATCH(
        "/api/admin/yards/zones/reorder",
        { body: { buildingId, type, orderedIds } },
      );
      if (apiError) {
        toaster.error({
          title: "Erreur",
          description: "Échec du réordonnancement des zones",
        });
        fetchData();
      }
    });
  };

  const confirmDeleteBuilding = async () => {
    if (!buildingToDelete) return;
    setIsDeleting(true);
    const { error: apiError } = await apiClient.DELETE(
      "/api/admin/yards/buildings/{id}",
      { params: { path: { id: buildingToDelete.id } } },
    );
    setIsDeleting(false);
    if (apiError) {
      toaster.error({
        title: "Erreur",
        description: apiError.error || "Échec de la suppression du bâtiment",
      });
      return;
    }
    toaster.success({
      title: "Succès",
      description: "Bâtiment supprimé avec succès",
    });
    setBuildingToDelete(null);
    fetchData();
  };

  const confirmDeleteZone = async () => {
    if (!zoneToDelete) return;
    setIsDeleting(true);
    const { error: apiError } = await apiClient.DELETE(
      "/api/admin/yards/zones/{id}",
      { params: { path: { id: zoneToDelete.id } } },
    );
    setIsDeleting(false);
    if (apiError) {
      toaster.error({
        title: "Erreur",
        description: apiError.error || "Échec de la suppression de la zone",
      });
      return;
    }
    toaster.success({
      title: "Succès",
      description: "Zone supprimée avec succès",
    });
    setZoneToDelete(null);
    fetchData();
  };

  const buildingHasZones = (buildingToDelete?.zones.length ?? 0) > 0;

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-gray-500">
        Chargement...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <YardGeographyTable
        draftActions={
          isDirty && (
            <>
              <span className="text-sm text-amber-600 dark:text-amber-400">
                Modifications non enregistrées
              </span>
              <Button
                outline
                onClick={() => void confirmDiscardDraft(cancelDraft)}
                disabled={isSaving}
              >
                Annuler
              </Button>
              <Button onClick={handleSaveClick} disabled={isSaving}>
                {isSaving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </>
          )
        }
        buildings={buildings}
        getBuildingDraft={getBuildingDraft}
        isBuildingDirty={isBuildingDirty}
        onBuildingFieldChange={onBuildingFieldChange}
        getZoneDraft={getZoneDraft}
        isZoneDirty={isZoneDirty}
        onZoneFieldChange={onZoneFieldChange}
        onDeleteBuilding={(b) => setBuildingToDelete(b)}
        onDeleteZone={(z) => setZoneToDelete(z)}
        onReorderBuildings={reorderBuildings}
        onReorderZones={reorderZones}
        newBuildings={newBuildings}
        onNewBuildingFieldChange={onNewBuildingFieldChange}
        onRemoveNewBuilding={onRemoveNewBuilding}
        onAddBuildingRow={onAddBuildingRow}
        newZonesByBuilding={newZonesByBuilding}
        onNewZoneFieldChange={onNewZoneFieldChange}
        onRemoveNewZone={onRemoveNewZone}
        onAddZoneRow={onAddZoneRow}
      />

      {/* Building deletion: two distinct outcomes. */}
      <Modal.Root
        open={isNotNil(buildingToDelete)}
        onOpenChange={(details) => {
          if (!details.open) setBuildingToDelete(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>
                  {buildingHasZones
                    ? "Supprimez d'abord les zones"
                    : "Confirmer la suppression"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  {buildingHasZones
                    ? `Le bâtiment « ${buildingToDelete?.name} » contient des zones. Supprimez les zones de ce bâtiment pour pouvoir ensuite le supprimer.`
                    : `Vous êtes sur le point de supprimer le bâtiment « ${buildingToDelete?.name} » ainsi que les contrats qui y sont rattachés. Cette action est irréversible.`}
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button outline onClick={() => setBuildingToDelete(null)}>
                  {buildingHasZones ? "Fermer" : "Annuler"}
                </Button>
                {!buildingHasZones && (
                  <Button
                    color="red"
                    onClick={confirmDeleteBuilding}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Suppression..." : "Supprimer"}
                  </Button>
                )}
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      {/* Zone deletion: only while in preparation. */}
      <Modal.Root
        open={isNotNil(zoneToDelete)}
        onOpenChange={(details) => {
          if (!details.open) setZoneToDelete(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>
                  {inPreparation
                    ? "Confirmer la suppression"
                    : "Suppression impossible"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  {inPreparation
                    ? `Vous êtes sur le point de supprimer la zone « ${zoneToDelete?.name} » (${
                        zoneToDelete ? ZONE_TYPE_LABELS[zoneToDelete.type] : ""
                      }) ainsi que les interventions et affectations qui y sont rattachées. Cette action est irréversible.`
                    : "Une zone ne peut être supprimée que pendant la période de préparation du chantier."}
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button outline onClick={() => setZoneToDelete(null)}>
                  {inPreparation ? "Annuler" : "Fermer"}
                </Button>
                {inPreparation && (
                  <Button
                    color="red"
                    onClick={confirmDeleteZone}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Suppression..." : "Supprimer"}
                  </Button>
                )}
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      {/* Confirmation when saving edits or reordering on a yard no longer in preparation. */}
      <Modal.Root
        open={isNotNil(pendingModification)}
        onOpenChange={(details) => {
          if (!details.open) setPendingModification(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Confirmer la modification</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  Le chantier n'est plus en période de préparation.
                  Confirmez-vous {pendingModification?.description} ?
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button outline onClick={() => setPendingModification(null)}>
                  Annuler
                </Button>
                <Button
                  onClick={() => {
                    const action = pendingModification?.run;
                    setPendingModification(null);
                    void action?.();
                  }}
                >
                  Enregistrer
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </div>
  );
}
